import cx from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getPositionOnViewport } from '~/helpers/position';

import styles from './Tooltip.scss';
import TooltipManager from './TooltipManager';

import type { ReactNode } from 'react';

/**
 * !!! IMPORTANT !!!
 *
 * This is legacy tooltip component and it is used in various parts of the application.
 * It manages tooltips activation on elements with data attributes.
 *
 * New tooltips should use DivTooltip from wows-react-ui-kit.
 */

const CURSOR_WIDTH = 18;
const CURSOR_HEIGHT = 27;
const ELEMENT_OFFSET_X = 4;
const ELEMENT_OFFSET_Y = 2;
const TOOLTIP_SHOW_DELAY = 50;

interface TooltipProps {
  children?: ReactNode;
  id: string;
  width?: string;
  isDemo?: boolean;
  getContent?: (arg: ReactNode) => ReactNode;
}

interface TooltipState {
  isVisible: boolean;
  top: number;
  left: number;
  tooltipTip: ReactNode | null;
}

const Tooltip = ({ children, id, width, isDemo = false, getContent }: TooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const timeoutIdRef = useRef<number | null>(null);

  const [state, setState] = useState<TooltipState>({
    isVisible: false,
    top: 0,
    left: 0,
    tooltipTip: null,
  });

  const clearTimeouts = useCallback(() => {
    if (timeoutIdRef.current != null) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    setState((s) => ({ ...s, isVisible: true }));
  }, []);

  const hide = useCallback(() => {
    clearTimeouts();
    setState((s) => ({
      ...s,
      isVisible: false,
      tooltipTip: null,
    }));
  }, [clearTimeouts]);

  const setPosition = useCallback((e: MouseEvent) => {
    if (!tooltipRef.current) {
      return;
    }

    const initialPosition = { top: e.clientY, left: e.clientX };

    const position = getPositionOnViewport(tooltipRef.current, initialPosition, {
      CURSOR_WIDTH,
      CURSOR_HEIGHT,
      ELEMENT_OFFSET_X,
      ELEMENT_OFFSET_Y,
    });

    setState((s) => ({ ...s, ...position }));
  }, []);

  const activateRef = useRef<(tip: ReactNode) => void>(() => {});
  const moveRef = useRef<(e: MouseEvent) => void>(() => {});
  const hideRef = useRef<() => void>(() => {});

  useEffect(() => {
    activateRef.current = (tooltipTip: ReactNode) => {
      clearTimeouts();

      setState((s) => ({
        ...s,
        tooltipTip,
      }));

      timeoutIdRef.current = window.setTimeout(show, TOOLTIP_SHOW_DELAY);
    };

    moveRef.current = (e: MouseEvent) => {
      setPosition(e);
    };

    hideRef.current = () => {
      hide();
    };
  }, [clearTimeouts, hide, setPosition, show]);

  // Stable controller object for manager
  const controller = useMemo(
    () => ({
      activate: (tooltipTip: ReactNode) => activateRef.current(tooltipTip),
      move: (e: MouseEvent) => moveRef.current(e),
      hide: () => hideRef.current(),
    }),
    [],
  );

  useEffect(() => {
    TooltipManager.addTooltip(id, controller);

    return () => {
      clearTimeouts();
      TooltipManager.removeTooltip(id);
    };
  }, [id, controller, clearTimeouts]);

  const classNameTooltip = cx(styles.tooltip, {
    [styles.isVisible]: state.isVisible,
    [styles.isDemo]: isDemo,
  });

  const style = useMemo(
    () =>
      ({
        top: state.top,
        left: state.left,
        maxWidth: width ?? null,
      }) as React.CSSProperties,
    [state.top, state.left, width],
  );

  const content = children ?? (state.tooltipTip && getContent ? getContent(state.tooltipTip) : null);

  const tooltipNode = (
    <div className={classNameTooltip} style={style} ref={tooltipRef}>
      {content}
    </div>
  );

  return isDemo ? tooltipNode : createPortal(tooltipNode, document.body);
};

export default memo(Tooltip);
